import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { create } from "../../api/API";
import { GoogleLogin } from "@react-oauth/google";
import { FaEye, FaEyeSlash, FaTimesCircle } from "react-icons/fa";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import { loginUser } from "../../utility/UserUtils";

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    if (!email || !password) return "Email and Password are required";
    if (password.length < 6) return "Password must be at least 6 characters";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errMsg = validate();
    if (errMsg) return toast.error(errMsg);

    try {
      setLoading(true);
      const res = await create("/members/login", { email, password });

      if (!res.user?._verified) {
        toast.error("Your account is registered, please verify your email.");
        setUnverified(true);
        return;
      }

      loginUser(res.user, res.token);
      if (onLogin) onLogin({ ...res.user, token: res.token });
      window.dispatchEvent(new Event("authChange"));
      toast.success("Logged in successfully!");
      navigate("/");
    } catch (err) {
      const responseData = err.response?.data;
      const message =
        responseData?.message || responseData?.errors?.[0]?.message;

      if (message?.toLowerCase().includes("verify")) {
        toast.error(message);
        setUnverified(true);
      } else {
        toast.error(message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) return toast.error("Please enter your email first.");
    try {
      await create("resend-email-verification", { email });
      toast.success("Verification email resent! Please check your inbox.");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0]?.message ||
        "Failed to resend email.";
      toast.error(message);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const token = credentialResponse.credential;
      const res = await create("/auth/google-login", { token });

      // SUCCESS -- normal login
      loginUser(res.user, res.token);
      if (onLogin) onLogin({ ...res.user, token: res.token });
      window.dispatchEvent(new Event("authChange"));
      toast.success("Logged in with Google!");
      navigate("/");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.message ||
        "Google login failed. Please try again.";

      toast.error(errorMessage);

      // If user not found, redirect to signup with Google token
      const isNewUser =
        errorMessage?.toLowerCase().includes("not found") ||
        errorMessage?.toLowerCase().includes("no user") ||
        errorMessage?.toLowerCase().includes("does not exist") ||
        errorMessage?.toLowerCase().includes("not registered") ||
        err.response?.status === 404;

      if (isNewUser) {
        navigate("/signup", {
          state: { token: credentialResponse.credential, via: "google" },
        });
      }
    }
  };

  return (
    <div
      className="auth-page"
      style={{ backgroundImage: `url("../assets/images/loginsignbg.svg")` }}
    >
      <div className="auth-box1 bg-white">
        <h2 className="auth-title text-center">Have an account? Login</h2>
        <p className="auth-subtext text-black text-center">
          *Required fields are marked with an asterisk
        </p>

        <form onSubmit={handleSubmit}>
          {/* Email Input */}
          <div className="input-wrapper bg-white">
            <FloatingLabelInput
              label="Email*"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {email && (
              <span
                className="clear-icon text-black"
                onClick={() => setEmail("")}
              >
                <FaTimesCircle />
              </span>
            )}
          </div>

          {/* Password Input */}
          <div className="input-wrapper bg-white">
            <FloatingLabelInput
              label="Password*"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span
              className="password-icon text-black"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {password && (
              <span
                className="clear-icon text-black"
                onClick={() => setPassword("")}
              >
                <FaTimesCircle />
              </span>
            )}
          </div>

          {/* Links */}
          <div className="auth-links flex justify-between items-center text-primary mt-2">
            <p
              type="button"
              className="text-blue-600 underline cursor-pointer"
              onClick={() => navigate("/reset-password")}
            >
              Forgot Password?
            </p>

            {/* {unverified && (
              <p
                type="button"
                className="text-blue-600 underline cursor-pointer"
                onClick={handleResendVerification}
              >
                Resend Verification Email
              </p>
            )} */}
          </div>

          <button
            type="submit"
            className="auth-btn bg-primary text-white"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        {/* Divider */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* Google Login */}
        <div className="auth-socials">
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={() => toast.error("Google login failed")}
          />
        </div>

        <p className="auth-footer">Don’t have an account yet?</p>
        <p>
          <Link to="/signup">Join today</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
